import React, { PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import {
    MappedComponentProperties,
} from '@adobe/aem-react-editable-components';

import './OneTimePasscode.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import SessionStorage from '../../utils/SessionStorage';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { AcquisitionFlow, AcquisitionFlowMap, clearSessionAndRedirectToDataEntryPage, getAcquisitionFlow, submitConsent, getQueryParams, getAppData } from '../../utils/acquisition-utils';
import { cleanInternalLinkUrl, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { flowDataModule } from '../../utils/flowDataModule';
import { DynamicResponse } from '../../utils/api-utils/api-utils';
import { checkForPhoneNumber, replaceWithCustomerNumber } from '../../providers/MitekProvider/MitekUtils';
import { AcquisitionsLoader } from '../AcquisitionsLoader/AcquisitionsLoader';
import { callAcquisitionBff } from '../../utils/acquisition-api/AcquisitionBff';
import axios from 'axios';
import { Button, Row, Col, Container } from '../../importCL';

interface IOneTimePasscodeResponse {
    IsError?: boolean;
    ErrorMessage?: string;
    SubmitApplicationEnum?: number;
}

interface IOneTimePasscodeProps extends MappedComponentProperties {
    title?: string;
    description?: string;
    altDescription?: string;
    ctaText?: string;
    ctaLink?: string;
    step2?: boolean;
    entryPageDescription?: string;
    entryPageAltDescription?: string;
    bypassInstructions?: string;

    apiErrorCode?: string;
    otpVerifyErrorCode?: string;
    limitedEsignUrl?: string;
    riskBasedEsignUrl?: string;
    confirmationPageUrl?: string;
    pendingApprovalPageUrl?: string;
    levelThreePageUrl?: string;
    customerDocumentationVerifyPageUrl?: string;
    mobileWalletApprovedUrl?: string;
    declinedUrl?: string;
    approvedPendingFundingUrl?: string;
}

interface IApprovedDataResponse {
    InstantIssuanceOffer?: number;
    AdditionalInformation?: IAdditionalInformation;
}

interface IPendingDataResponse {
    AdditionalInformation?: IAdditionalInformation;
}

interface IAuthorizedDataResponse {
    NextAction?: number;
}

interface IAdditionalInformation {
    HasCheckDebtAccount?: string;
    CreditLevel?: string;
    EmploymentStatus?: string;
    HousingStatus?: string;
    HasMoneyAccount?: string;
    GenericQuestionId1?: number;
    GenericAnswer1?: string;
    GenericQuestionId2?: number;
    GenericAnswer2?: string;
}
export const OneTimePasscodeEditConfig = {
    emptyLabel: 'One Time Passcode',
    isEmpty: (props: PropsWithChildren<IOneTimePasscodeProps>) => {
        return !props.title;
    },
};

const OneTimePasscode = (
    props: PropsWithChildren<IOneTimePasscodeProps>,
): JSX.Element => {
    const { showPushdown } = usePushdownContext();
    const flow = getAcquisitionFlow();

    const [oneTimePasscode, setOneTimePasscode] = useState('');
    const [firstPlace, setFirstPlace] = useState('');
    const [secondPlace, setSecondPlace] = useState('');
    const [thirdPlace, setThirdPlace] = useState('');
    const [fourthPlace, setFourthPlace] = useState('');
    const [fifthPlace, setFifthPlace] = useState('');
    const [sixthPlace, setSixthPlace] = useState('');
    const [isSpinning, setIsSpinning] = useState<boolean>(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState();
    const description = props.description ? props.description : 'For security purposes we\'ll need to verify your identity by sending a verification code to the mobile number: XXX-XXX-XXXX';
    const entryPageDescription = props.entryPageDescription ? props.entryPageDescription : 'Please enter the 6-digit code we just sent to XXX-XXX-XXXX';

    const updatedDescription = replaceWithCustomerNumber(phoneNumber, description, props.altDescription);
    const updatedEntryPageDescription = replaceWithCustomerNumber(phoneNumber, entryPageDescription, props.entryPageAltDescription);

    let appApprovedURL = '';
    let appPendingURL = '';

    const inputOne = useRef<HTMLInputElement>(null);
    const inputTwo = useRef<HTMLInputElement>(null);
    const inputThree = useRef<HTMLInputElement>(null);
    const inputFour = useRef<HTMLInputElement>(null);
    const inputFive = useRef<HTMLInputElement>(null);
    const inputSix = useRef<HTMLInputElement>(null);

    useEffect(() => {
        checkForPhoneNumber().then(possiblePhoneNumber => {
            setPhoneNumber(possiblePhoneNumber ? possiblePhoneNumber.replace(/(.{3})(.{3})(.*)/, '$1-$2-$3') : '');
        });
    }, []);

    useEffect(() => {
        setPlaces();
    }, [
        firstPlace,
        secondPlace,
        thirdPlace,
        fourthPlace,
        fifthPlace,
        sixthPlace,
    ]);

    useEffect(() => {
        enableSubmit(oneTimePasscode);
    }, [oneTimePasscode]);


    function setPlaces() {
        setOneTimePasscode(
            firstPlace +
            secondPlace +
            thirdPlace +
            fourthPlace +
            fifthPlace +
            sixthPlace,
        );
    }

    function enableSubmit(oneTimePasscode: string) {
        if (oneTimePasscode.length > 5) {
            setIsEnabled(true);
        } else if (oneTimePasscode.length < 6 && isEnabled === true) {
            setIsEnabled(false);
        }
    }

    async function approvedDataSuccess(approvedDataResponse: IApprovedDataResponse) {
        const instantIssuanceOffer = approvedDataResponse.InstantIssuanceOffer;
        await SessionStorage.setCipher( 'app-approved-data', JSON.stringify(approvedDataResponse));
        await flowDataModule.updateInSession(
            approvedDataResponse.AdditionalInformation,
        );
        const appData = await getAppData();

        if (appData?.IsSecuredCard) {
            window.location.href = cleanInternalLinkUrl(
                props.approvedPendingFundingUrl +
                (getQueryParams().length ? getQueryParams() : ''),
            );
            return;
        }

        switch (instantIssuanceOffer) {
            case 0:
                window.location.href = cleanInternalLinkUrl(
                    props.confirmationPageUrl +
                    (getQueryParams().length ? getQueryParams() : ''),
                );
                break;
            case 2:
                window.location.href = cleanInternalLinkUrl(
                    props.mobileWalletApprovedUrl +
                    (getQueryParams().length ? getQueryParams() : ''),
                );
                break;
            default:
                clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Default instant issuance offer error');
                break;
        }
    }

    async function pendingDataSuccess(pendingDataResponse: IPendingDataResponse) {
        await SessionStorage.setCipherWithCurrentScope(
            'app-pending-data',
            pendingDataResponse,
        );
        await flowDataModule.updateInSession(
            pendingDataResponse.AdditionalInformation,
        );
        window.location.href =
            props.pendingApprovalPageUrl +
            (getQueryParams().length ? getQueryParams() : '');
    }

    async function authorizedDataSuccess(
        authorizedDataResponse: IAuthorizedDataResponse,
    ) {
        const nextAction = authorizedDataResponse.NextAction;
        await SessionStorage.setCipherWithCurrentScope(
            'appAuthorizedResponse',
            authorizedDataResponse,
        );
        switch (nextAction) {
            case 0:
                clearSessionAndRedirectToDataEntryPage('appInProgress');
                return;
            case 1:
                window.location.href = cleanInternalLinkUrl(
                    props.levelThreePageUrl +
                    (getQueryParams().length ? getQueryParams() : ''),
                );
                break;
            case 3:
            case 4:
                window.location.href = cleanInternalLinkUrl(
                    props.customerDocumentationVerifyPageUrl +
                    (getQueryParams().length ? getQueryParams() : ''),
                );
                break;
            case 2:
                return;
            default:
                clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Default application authorization error');
                break;
        }
    }

    function bypassOneTimePasscode() {
        acquisitionApiRequest<IOneTimePasscodeResponse>('bypass-verification', {})
            .then(response => {
                handleResponse(response);
            })
            .catch(() => {
                flowDataError();
            });
    }

    function flowDataError() {
        showPushdown(props.apiErrorCode as string);
        setIsSpinning(false);
    }

    function validateOTP(oneTimePasscode?: string) {
        setIsSpinning(true);
        setIsEnabled(false);
        if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
            const flowApplicationData= SessionStorage.tryGetObject('flowApplicationData', '{}');
            callAcquisitionBff('stepup-authorization', {
                replace: { applicationId: flowApplicationData.application.applicationId },
                body: {
                    application: {
                        stepUpType: 'OTP',
                        otp: oneTimePasscode,
                    },
                },
            }).then(response => {
                handleBffResponse(response);
            }).catch((error) => {
                if (axios.isAxiosError(error) && error.response?.status !== 200) {
                    clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Axios error or bad response code');
                }
                console.error('OTP validation failed');
            });
        } else {
            acquisitionApiRequest<IOneTimePasscodeResponse>('otp-verification', {
                method: 'POST',
                body: {
                    Passcode: oneTimePasscode,
                },
            }).then(response => {
                handleResponse(response);
            });
        }
    }

    function handleResponse(
        response: DynamicResponse<IOneTimePasscodeResponse>,
    ) {
        if (response.data.IsError) {
            showPushdown(props.otpVerifyErrorCode as string);
            setIsSpinning(false);
        } else {
            switch (response.data.SubmitApplicationEnum) {
                case 0: //verification failed
                    clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Verification failed');
                    break;
                case 1: //Decline with ESign Credit
                case 2: //Decline with ESign Credit no Score
                case 3: //Decline with ESign non credit
                case 4: //Decline with ESign NCD Letter
                case 5: //Decline with ESign MLA
                case 6: //Decline with ESign O fad
                    window.location.href = cleanInternalLinkUrl(
                        (props.limitedEsignUrl +
                            document.location.search) as string,
                    );
                    break;
                case 7: //Approved
                    appApprovedURL = ({
                        preQual: 'pq-app-approved',
                        preApproved: 'pa-app-approved',
                        skipPQ: 'skip-pq-app-approved',
                        pqOne: 'skip-pq-app-approved',
                        partnerHosted: '',
                    } as AcquisitionFlowMap)[flow];
                    acquisitionApiRequest<IApprovedDataResponse>(
                        appApprovedURL,
                        { method: 'GET' },
                    )
                        .then(approvedDataResponse => {
                            approvedDataSuccess(approvedDataResponse.data);
                        })
                        .catch(() => {
                            flowDataError();
                        });
                    break;
                case 8: //Pending
                    appPendingURL = ({
                        preQual: 'pq-app-pending',
                        preApproved: 'pa-app-pending',
                        skipPQ: 'skip-pq-app-pending',
                        pqOne: 'skip-pq-app-pending',
                        partnerHosted: '',
                    } as AcquisitionFlowMap)[flow];
                    acquisitionApiRequest<IPendingDataResponse>(appPendingURL, {
                        method: 'GET',
                    })
                        .then(async (pendingDataResponse) => {
                            await pendingDataSuccess(pendingDataResponse.data);
                        })
                        .catch(() => {
                            flowDataError();
                        });
                    break;
                case 9: //Authorized
                    acquisitionApiRequest<IAuthorizedDataResponse>(
                        'get-application-authorized',
                        { method: 'GET' },
                    )
                        .then(pendingDataResponse => {
                            authorizedDataSuccess(pendingDataResponse.data);
                        })
                        .catch(() => {
                            flowDataError();
                        });
                    break;
                case 10: //Cancel
                case 11: //Error
                    clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Cancelled or Errored submit response');
                    break;
                case 12: //InProcess
                case 13: //Pending SSN
                case 14: //Queued
                    window.location.href = cleanInternalLinkUrl(
                        (props.pendingApprovalPageUrl +
                            document.location.search) as string,
                    );
                    break;
                case 15: //OK
                    clearSessionAndRedirectToDataEntryPage('lost', undefined, 'OK submit response');
                    break;
            }
        }
    }

    async function handleBffResponse(response: any) {
        if (!response?.data?.applicationStatus?.applicationStatus) {
            showPushdown(props.otpVerifyErrorCode);
            setIsSpinning(false);
            return;
        }
        let flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', {});
        flowApplicationData = {
            ...flowApplicationData,
            applicationStatus: response.data.applicationStatus.applicationStatus || flowApplicationData.applicationStatus,
            statusDescription: response.data.applicationStatus.statusDescription || flowApplicationData.statusDescription,
            letter: response.data?.letter,
            stepUpType: response.data.stepUpAuthorization.stepUpType || flowApplicationData.stepUpType,
        };
        SessionStorage.setObject('flowApplicationData', flowApplicationData);
        const consent = await submitConsent(flowApplicationData);

        switch (flowApplicationData.applicationStatus.toUpperCase()) {
            case 'APPROVED':
                consent ? window.location.assign(cleanInternalLinkUrl(
                    (props.confirmationPageUrl + document.location.search),
                )) : clearSessionAndRedirectToDataEntryPage('processingError');
                break;
            case 'DECLINED':
            case 'SOFTDECLINE':
            case 'HARDDECLINE':
                consent ? window.location.assign(cleanInternalLinkUrl(
                    (props.declinedUrl + document.location.search),
                )) : clearSessionAndRedirectToDataEntryPage('processingError');
                break;
            case 'PENDING':
            case 'QUEUED':
                consent ? window.location.assign(cleanInternalLinkUrl(
                    (props.pendingApprovalPageUrl + document.location.search),
                )) : clearSessionAndRedirectToDataEntryPage('processingError');
                break;
            default:
                clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Unsupported application status response');
                break;
        }
    }

    function handleOtpAutofill(event: React.ClipboardEvent<HTMLInputElement>) {
        const otpString = event.clipboardData.getData('Text');
        if (otpString.length === 6) {
            setFirstPlace(otpString[0]);
            setSecondPlace(otpString[1]);
            setThirdPlace(otpString[2]);
            setFourthPlace(otpString[3]);
            setFifthPlace(otpString[4]);
            setSixthPlace(otpString[5]);
            inputSix.current?.focus();
        }
    }

    return (
        isSpinning ? <AcquisitionsLoader processingSteps={[]} />
            :
            <div id='one-time-passcode' className='OneTimePasscode'>
                <Container
                    className={
                        props.step2
                            ? 'OneTimePasscode__send--hide'
                            : 'OneTimePasscode__send'
                    }
                >
                    <Row>
                        <Col className='OneTimePasscode__title' md={12} lg={12}>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}
                        </Col>
                    </Row>
                    <Row className='OneTimePasscode__Row--description'>
                        <Col
                            className='OneTimePasscode__description'
                            md={12}
                            lg={12}
                        >
                            {transformHTMLParserLinks(updatedDescription)}
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            className='OneTimePasscode__buttonContainer'
                            md={12}
                            lg={12}
                        >
                            <Button
                                className='OneTimePasscode__button'
                                onClick={() => {
                                    window.location.href = `${props.ctaLink}${window.location.search}`;
                                }}
                            >
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.ctaText)) as unknown as ReactElement}
                            </Button>
                        </Col>
                    </Row>
                </Container>

                <Container
                    className={
                        props.step2
                            ? 'OneTimePasscode__entry'
                            : 'OneTimePasscode__entry--hide'
                    }
                >
                    <Row>
                        <Col className='OneTimePasscode__title' md={12} lg={12}>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}
                        </Col>
                    </Row>
                    <Row className='OneTimePasscode__Row--description'>
                        <Col
                            className='OneTimePasscode__description'
                            md={12}
                            lg={12}
                        >
                            {transformHTMLParserLinks(updatedEntryPageDescription as string)}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <section className='OneTimePasscode__form mb-30-small'>
                                <article className='small-12-centered'>
                                    <form id='input-text'>
                                        <input
                                            ref={inputOne}
                                            type='tel'
                                            className='OneTimePasscode__input inline text-center'
                                            name='1'
                                            size={1}
                                            maxLength={1}
                                            tabIndex={1}
                                            id='input_component_1'
                                            value={firstPlace}
                                            onPaste={event => handleOtpAutofill(event)}
                                            onChange={event => {
                                                if (!isNaN(parseInt(event.target.value)) || event.target.value === '') {
                                                    setFirstPlace(
                                                        event.target.value.toString(),
                                                    );
                                                    if (inputTwo.current && event.target.value.length === event.target.maxLength) {
                                                        inputTwo.current.focus();
                                                    }
                                                }

                                            }}
                                            autoComplete='one-time-code'
                                        />
                                        <input
                                            ref={inputTwo}
                                            type='tel'
                                            className='OneTimePasscode__input inline text-center'
                                            name='2'
                                            size={1}
                                            maxLength={1}
                                            tabIndex={2}
                                            id='input_component_2'
                                            value={secondPlace}
                                            onChange={event => {
                                                if (!isNaN(parseInt(event.target.value)) || event.target.value === '') {
                                                    setSecondPlace(
                                                        event.target.value.toString(),
                                                    );
                                                    if (inputThree.current && event.target.value.length === event.target.maxLength) {
                                                        inputThree.current.focus();
                                                    }
                                                }

                                            }}
                                            autoComplete='one-time-code'
                                        />
                                        <input
                                            ref={inputThree}
                                            type='tel'
                                            className='OneTimePasscode__input inline text-center'
                                            name='3'
                                            size={1}
                                            maxLength={1}
                                            tabIndex={3}
                                            id='input_component_3'
                                            value={thirdPlace}
                                            onChange={event => {
                                                if (!isNaN(parseInt(event.target.value)) || event.target.value === '') {
                                                    setThirdPlace(
                                                        event.target.value.toString(),
                                                    );
                                                    if (inputFour.current && event.target.value.length === event.target.maxLength) {
                                                        inputFour.current.focus();
                                                    }
                                                }

                                            }}
                                            autoComplete='one-time-code'
                                        />
                                        <input
                                            ref={inputFour}
                                            type='tel'
                                            className='OneTimePasscode__input inline text-center'
                                            name='4'
                                            size={1}
                                            maxLength={1}
                                            tabIndex={4}
                                            id='input_component_4'
                                            value={fourthPlace}
                                            onChange={event => {
                                                if (!isNaN(parseInt(event.target.value)) || event.target.value === '') {
                                                    setFourthPlace(
                                                        event.target.value.toString(),
                                                    );
                                                    if (inputFive.current && event.target.value.length === event.target.maxLength) {
                                                        inputFive.current.focus();
                                                    }
                                                }

                                            }}
                                            autoComplete='one-time-code'
                                        />
                                        <input
                                            ref={inputFive}
                                            type='tel'
                                            className='OneTimePasscode__input inline text-center'
                                            name='5'
                                            size={1}
                                            maxLength={1}
                                            tabIndex={5}
                                            id='input_component_5'
                                            value={fifthPlace}
                                            onChange={event => {
                                                if (!isNaN(parseInt(event.target.value)) || event.target.value === '') {
                                                    setFifthPlace(
                                                        event.target.value.toString(),
                                                    );
                                                    if (inputSix.current && event.target.value.length === event.target.maxLength) {
                                                        inputSix.current.focus();
                                                    }
                                                }

                                            }}
                                            autoComplete='one-time-code'
                                        />
                                        <input
                                            ref={inputSix}
                                            type='tel'
                                            className='OneTimePasscode__input inline text-center'
                                            name='6'
                                            size={1}
                                            maxLength={1}
                                            tabIndex={6}
                                            value={sixthPlace}
                                            onChange={event => {
                                                if (!isNaN(parseInt(event.target.value)) || event.target.value === '') {
                                                    setSixthPlace(
                                                        event.target.value.toString(),
                                                    );
                                                }
                                            }}
                                            id='input_component_6'
                                            autoComplete='one-time-code'
                                        />
                                    </form>
                                </article>
                            </section>
                        </Col>
                    </Row>
                    <Row>
                        <Col
                            className='OneTimePasscode__submitButton'
                            md={12}
                            lg={12}
                        >
                            <Button
                                className='OneTimePasscode__button'
                                variant={isEnabled ? 'solid' : 'inactive'}
                                disabled={!isEnabled}
                                onClick={() => {
                                    validateOTP(oneTimePasscode);
                                }}
                            >
                                {isSpinning ? (
                                    <>
                                        Submit&nbsp;&nbsp;&nbsp;
                                        <svg className='loader' />
                                    </>
                                ) : (
                                    <>
                                        Submit
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <section className='mb-100-small'>
                                <article className='small-12 text-center OneTimePasscode__resendCode'>
                                    <div>
                                        <a
                                            className='OneTimePasscode__byPass'
                                            onClick={() => {
                                                bypassOneTimePasscode();
                                            }}
                                        >
                                            {transformHTMLParserLinks(
                                                props.bypassInstructions as string,
                                            )}
                                        </a>
                                    </div>
                                </article>
                            </section>
                        </Col>
                    </Row>
                </Container>
            </div>
    );
};

export default OneTimePasscode;
